/**
 * Giraffe global ID (GR-XXXXXX-C) encode, strict parse and lenient normalise.
 * The check character is Luhn mod 32 over the Crockford alphabet (SA-01 §5.1c).
 * Normative spec: shared/global-id/SPEC.md. Must agree with shared/global-id/test-vectors.json.
 * Sequences are bigint because IDs can encode values up to 2^63 - 1.
 */

const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const PREFIX = "GR";
const MIN_PAYLOAD = 6;
const MAX_PAYLOAD = 13;
const MAX_SEQUENCE = (1n << 63n) - 1n;

function isAscii(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) > 0x7f) return false;
  }
  return true;
}

export type GlobalIdError = "format" | "range" | "check";

export type GlobalIdResult =
  | { valid: true; globalId: string; sequence: bigint }
  | { valid: false; error: GlobalIdError };

/** Luhn mod 32: right to left with factors 2, 1, 2, …; check = (32 − sum mod 32) mod 32. Payload must be in the alphabet. */
export function checkCharacterFor(payload: string): string {
  let factor = 2;
  let total = 0;
  for (let i = payload.length - 1; i >= 0; i--) {
    const product = ALPHABET.indexOf(payload.charAt(i)) * factor;
    total += Math.floor(product / 32) + (product % 32);
    factor = factor === 2 ? 1 : 2;
  }
  return ALPHABET.charAt((32 - (total % 32)) % 32);
}

export function encodeGlobalId(sequence: bigint): string {
  if (sequence < 1n || sequence > MAX_SEQUENCE) {
    throw new RangeError("A global ID sequence must be between 1 and 2^63 - 1.");
  }
  let digits = "";
  for (let n = sequence; n > 0n; n /= 32n) {
    digits = ALPHABET.charAt(Number(n % 32n)) + digits;
  }
  const payload = digits.padStart(MIN_PAYLOAD, "0");
  return `${PREFIX}-${payload}-${checkCharacterFor(payload)}`;
}

/** Strict: the canonical form only. */
export function parseGlobalId(text: string): GlobalIdResult {
  if (!isAscii(text) || !text.startsWith(`${PREFIX}-`)) {
    return fail("format");
  }
  const rest = text.slice(PREFIX.length + 1);
  if (rest.length < MIN_PAYLOAD + 2 || rest.charAt(rest.length - 2) !== "-") {
    return fail("format");
  }
  const payload = rest.slice(0, -2);
  const check = rest.charAt(rest.length - 1);
  if (payload.length > MAX_PAYLOAD || !ALPHABET.includes(check)) {
    return fail("format");
  }
  if ([...payload].some((c) => !ALPHABET.includes(c))) {
    return fail("format");
  }
  if (payload.length > MIN_PAYLOAD && payload.startsWith("0")) {
    return fail("format");
  }
  let value = 0n;
  for (const c of payload) {
    value = value * 32n + BigInt(ALPHABET.indexOf(c));
  }
  if (value < 1n || value > MAX_SEQUENCE) {
    return fail("range");
  }
  if (checkCharacterFor(payload) !== check) {
    return fail("check");
  }
  return { valid: true, globalId: text, sequence: value };
}

/**
 * Lenient, for typed input: applies NFKC, removes space, tab, CR, LF and hyphens, upper-cases, maps O->0 and I/L->1,
 * removes surplus leading zeros, then parses strictly. A valid result is always canonical.
 */
export function normalizeGlobalId(text: string): GlobalIdResult {
  // NFKC: full-width Latin, digits and hyphens become ASCII (SA-01 §4.11).
  const compact = text.normalize("NFKC").replace(/[ \t\r\n-]/g, "");
  if (!isAscii(compact)) {
    return fail("format");
  }
  const upper = compact.toUpperCase();
  if (!upper.startsWith(PREFIX)) {
    return fail("format");
  }
  const rest = upper.slice(PREFIX.length);
  if (rest.length < MIN_PAYLOAD + 1 || rest.length > MAX_PAYLOAD + 1) {
    return fail("format");
  }
  const payload = mapAliases(rest.slice(0, -1)).replace(/^0+/, "").padStart(MIN_PAYLOAD, "0");
  const check = mapAliases(rest.slice(-1));
  return parseGlobalId(`${PREFIX}-${payload}-${check}`);
}

function mapAliases(value: string): string {
  return value.replace(/O/g, "0").replace(/[IL]/g, "1");
}

function fail(error: GlobalIdError): GlobalIdResult {
  return { valid: false, error };
}
